import { ComponentChild, ComponentChildren, createContext, h } from "preact";
import { useContext, useEffect, useState } from "preact/hooks";

interface SelectableListController<T> {
    select(item: T, ctrl: boolean, shift: boolean): void;
    selectAll(): void;
    deselectAll(): void;
    isSelected(item: T): boolean;
}

const SelectableListContext = createContext(null as SelectableListController<any> | null);

export function SelectableList<T = any>(props: {
    readonly children: ComponentChildren,
    readonly items: readonly T[],
    onChange: (items: readonly T[]) => void,
    value?: readonly T[],
}) {
    const [selectedItems, setSelectedItems] = useState(props.value ?? []);
    const [lastSelectedItemIndex, setLastSelectedLabelIndex] = useState(null as number | null);

    useEffect(() => {
        setSelectedItems(props.value ?? []);
    }, [props.value]);

    useEffect(() => {
        props.onChange(selectedItems);
    }, [selectedItems]);

    return <SelectableListContext.Provider children={props.children} value={{
        select: (item, ctrl, shift) => {
            const currentItemIndex = props.items.indexOf(item);
            const index = selectedItems.indexOf(item);

            if (index >= 0) {
                if (shift && selectedItems.length > 0 && lastSelectedItemIndex !== null) {
                    if (lastSelectedItemIndex >= 0) {
                        const output = [...selectedItems];
                        const diff = Math.sign(currentItemIndex - lastSelectedItemIndex);

                        for (let i = lastSelectedItemIndex; i !== currentItemIndex + diff; i += diff) {
                            const oldItem = props.items[i];
                            const oldItemIndex = output.indexOf(oldItem);

                            if (oldItemIndex < 0) {
                                continue;
                            }

                            output.splice(oldItemIndex, 1);
                        }

                        setSelectedItems(output);
                    }
                }
                else {
                    if (ctrl) {
                        setSelectedItems(selectedItems.filter(v => v !== selectedItems[index]));
                    }
                    else {
                        setSelectedItems([]);
                    }
                }
            }
            else {
                if (shift && selectedItems.length > 0 && lastSelectedItemIndex !== null) {
                    if (lastSelectedItemIndex >= 0) {
                        const output = [...selectedItems];
                        const diff = Math.sign(currentItemIndex - lastSelectedItemIndex);

                        for (let i = lastSelectedItemIndex; i !== currentItemIndex + diff; i += diff) {
                            const newItem = props.items[i];

                            if (output.indexOf(newItem) >= 0) {
                                continue;
                            }

                            output.push(newItem);
                        }

                        setSelectedItems(output);
                    }
                }
                else {
                    if (ctrl) {
                        setSelectedItems([
                            ...selectedItems,
                            item
                        ]);
                    }
                    else {
                        setSelectedItems([item]);
                    }
                }
            }

            setLastSelectedLabelIndex(currentItemIndex);
        },
        selectAll: () => {
            setSelectedItems(props.items);
        },
        deselectAll: () => {
            setSelectedItems([]);
        },
        isSelected: (item: any) => selectedItems.indexOf(item) >= 0
    }}/>
}

export function SelectableListItem<T>(props: {
    readonly children: (controller: SelectableListController<T>, item: T) => ComponentChild,
    readonly item: T
}) {
    return <SelectableListContext.Consumer children={v => props.children(v!, props.item)}/>;
}
